import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import {
  DiscussionCommitmentRating,
  DiscussionRoleRating,
  DiscussionSeatRating,
  ValueRating,
} from '../models/discussion.models';
import {
  DiscussionFormCustomQuestionAnswer,
  DiscussionFormMode,
  DiscussionFormValue,
} from '../models/form/discussion-form.model';

export type DiscussionFormValidationErrorKey = keyof DiscussionFormValue;
type DiscussionFormErrors = Partial<Record<DiscussionFormValidationErrorKey, unknown>>;

// TODO: Split this into separate validators for each control to prevent "expensive"
//  computations on fields that haven't changed

/** A Discussion form must have all rating sections filled out */
export function forbiddenDiscussionFormValidator(
  mode: DiscussionFormMode,
  displayManageeFormSections: boolean,
  displayManagerFormSections: boolean
): ValidatorFn {
  return (control: AbstractControl<DiscussionFormValue>): ValidationErrors | null => {
    const errors: DiscussionFormErrors = {};
    const { coreValues, roleRatings, leadership, coaching, customQuestions } =
      control.getRawValue() as DiscussionFormValue;

    let coreValueValidations: ValueRating[] = [];
    let roleRatingValidations: (DiscussionRoleRating | DiscussionSeatRating)[] = [];
    let leaderValidations: DiscussionCommitmentRating[] = [];
    let coachValidations: DiscussionCommitmentRating[] = [];
    let customQuestionsValidations: DiscussionFormCustomQuestionAnswer[] = [];

    if (displayManageeFormSections) {
      coreValueValidations = getRatingsToValidate(mode, coreValues.manager.ratings, coreValues.managee.ratings);
      roleRatingValidations = getRatingsToValidate(mode, roleRatings.manager.ratings, roleRatings.managee.ratings);
      customQuestionsValidations = getRatingsToValidate(
        mode,
        customQuestions.map(q => q.manager),
        customQuestions.map(q => q.managee)
      );
    }

    if (displayManagerFormSections) {
      leaderValidations = getRatingsToValidate(mode, leadership.manager.ratings, leadership.managee.ratings);
      coachValidations = getRatingsToValidate(mode, coaching.manager.ratings, coaching.managee.ratings);
    }

    validateCoreValues(coreValueValidations, errors);
    validateRoleRatings(roleRatingValidations, errors);
    validateLeaderCommitments(leaderValidations, errors);
    validateCoachingCommitments(coachValidations, errors);
    validateCustomQuestions(customQuestionsValidations, errors);

    const forbidden = Object.keys(errors).length > 0;
    return forbidden ? errors : null;
  };
}

function getRatingsToValidate<T>(mode: DiscussionFormMode, managerRatings: T[], manageeRatings: T[]): T[] {
  if (mode === DiscussionFormMode.Meeting) {
    return managerRatings.concat(manageeRatings);
  } else if (mode === DiscussionFormMode.Manager) {
    return managerRatings;
  } else if (mode === DiscussionFormMode.Managee) {
    return manageeRatings;
  }
  return [];
}

function validateCoreValues(ratings: ValueRating[], errors: DiscussionFormErrors): void {
  if (ratings.some(r => !r)) {
    errors['coreValues'] = { value: ratings.filter(r => !r) };
  }
}

function validateRoleRatings(
  ratings: DiscussionRoleRating[] | DiscussionSeatRating[],
  errors: DiscussionFormErrors
): void {
  if (ratings.some(r => !r.getsIt || !r.wantsIt || !r.hasCapacity)) {
    errors['roleRatings'] = { value: ratings.filter(r => !r.getsIt || !r.wantsIt || !r.hasCapacity) };
  }
}

function validateLeaderCommitments(ratings: DiscussionCommitmentRating[], errors: DiscussionFormErrors): void {
  if (ratings.some(r => !r.rating)) {
    errors['leadership'] = { value: ratings.filter(r => !r.rating) };
  }
}

function validateCoachingCommitments(ratings: DiscussionCommitmentRating[], errors: DiscussionFormErrors): void {
  if (ratings.some(r => !r.rating)) {
    errors['coaching'] = { value: ratings.filter(r => !r.rating) };
  }
}

function validateCustomQuestions(questions: DiscussionFormCustomQuestionAnswer[], errors: DiscussionFormErrors): void {
  const invalidQuestions = questions.map(q => q.answer).filter(a => !a);
  if (invalidQuestions.length > 0) {
    errors['customQuestions'] = { value: invalidQuestions };
  }
}
